import React from "react";
import {
  TEAM_ARCHIVED_BADGE,
  TEAM_ARCHIVED_HINT,
  TEAM_MOVE_NO_SALES,
  TEAM_MOVE_NO_TARGET,
  personLabel,
  salesCountLabel,
  teamSales,
} from "../../lib/teamManagement";

/**
 * ONE team: its name, its Manager and its active Търговци.
 *
 * The action row is rendered ONLY when `canManage` is true, which is only ever
 * true for an Owner. A Manager receives exactly the same card WITHOUT any
 * control — informational, because in Phase 2D.3A a Manager configures nothing.
 * The server refuses every one of these operations for a Manager regardless.
 *
 * An ARCHIVED team offers the Owner exactly TWO actions and no others (Phase
 * 2D.3A.2): възстанови, which brings it back after the server has re-validated its
 * Мениджър and its name, and изтрий, which removes it from this organization for
 * good while its history survives for audit. Renaming, staffing and moving stay
 * behind `team.is_active`, because an archived team has no membership to configure.
 */
export const TeamCard = ({ team, canManage, busy, hasMoveTarget, onRename, onChangeManager, onAdd, onMove, onRemove, onArchive, onRestore, onDelete }) => {
  const members = team.members || [];
  // A move starts HERE: it is offered only when this team actually has somebody
  // to move and there is another active team to move them into. An empty
  // selector is never opened.
  const movable = teamSales(team);
  const canMove = movable.length > 0 && hasMoveTarget === true;
  const moveBlocked = movable.length === 0 ? TEAM_MOVE_NO_SALES : TEAM_MOVE_NO_TARGET;
  return (
    <div className="qw-card" data-testid={`team-card-${team.id}`} style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <div className="card-title" data-testid={`team-name-${team.id}`}>{team.name}</div>
        {!team.is_active && (
          <span className="qw-badge" data-testid={`team-archived-${team.id}`}>
            {TEAM_ARCHIVED_BADGE}
          </span>
        )}
        <span style={{ marginLeft: "auto", fontSize: 13, color: "#475569" }} data-testid={`team-count-${team.id}`}>
          {salesCountLabel(team.member_count)}
        </span>
      </div>

      <div style={{ marginTop: 8, fontSize: 14 }} data-testid={`team-manager-${team.id}`}>
        Мениджър: <strong>{personLabel(team.manager) || "—"}</strong>
      </div>

      {members.length > 0 ? (
        <ul style={{ marginTop: 8, paddingLeft: 18 }} data-testid={`team-members-${team.id}`}>
          {members.map((m) => (
            <li key={m.member_id} data-testid={`team-member-${team.id}-${m.member_id}`}>
              {personLabel(m)}
              {canManage && team.is_active && (
                <button
                  type="button"
                  className="qw-btn ghost sm"
                  style={{ marginLeft: 8 }}
                  disabled={busy}
                  onClick={() => onRemove(team, m)}
                  data-testid={`team-member-remove-${m.member_id}`}
                >
                  Премахни
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <div className="qw-help" data-testid={`team-members-empty-${team.id}`}>
          Няма търговци в този екип.
        </div>
      )}

      {canManage && team.is_active && (
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          <button type="button" className="qw-btn ghost" disabled={busy} onClick={() => onRename(team)} data-testid={`team-rename-${team.id}`}>
            Редактирай
          </button>
          <button type="button" className="qw-btn ghost" disabled={busy} onClick={() => onAdd(team)} data-testid={`team-add-sales-${team.id}`}>
            Добави търговец
          </button>
          <button type="button" className="qw-btn ghost" disabled={busy || !canMove} title={canMove ? undefined : moveBlocked} onClick={() => onMove(team)} data-testid={`team-move-sales-${team.id}`}>
            Премести търговец
          </button>
          <button type="button" className="qw-btn ghost" disabled={busy} onClick={() => onChangeManager(team)} data-testid={`team-change-manager-${team.id}`}>
            Смени мениджър
          </button>
          <button type="button" className="qw-btn ghost" disabled={busy} onClick={() => onArchive(team)} data-testid={`team-archive-${team.id}`}>
            Архивирай
          </button>
        </div>
      )}

      {canManage && !team.is_active && (
        <div style={{ marginTop: 12 }}>
          <div className="qw-help" data-testid={`team-archived-hint-${team.id}`}>
            {TEAM_ARCHIVED_HINT}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            <button type="button" className="qw-btn ghost" disabled={busy} onClick={() => onRestore(team)} data-testid={`team-restore-${team.id}`}>
              Възстанови
            </button>
            <button type="button" className="qw-btn danger" disabled={busy} onClick={() => onDelete(team)} data-testid={`team-delete-${team.id}`}>
              Изтрий
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamCard;
